import React, { ComponentPropsWithRef, useRef, useState } from 'react';

import { IconSearch, IconX } from '@tabler/icons-react';
import { useClickOutside } from 'hooks/useClickOutside';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { TextField, TextFieldType, Typography } from '@cfa/react-components';
import { Surface, SurfaceProps } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import { SearchbarRotatingPromptPlaceholder } from './SearchbarRotatingPromptPlaceholder';
import SearchbarSuggestionsDropdown from './SearchbarSuggestionsDropdown';

import { searchAtom, useSearchDispatch } from '@/atoms/search';

interface SearchbarProps extends Pick<
  ComponentPropsWithRef<'div'>,
  'ref' | 'className'
> {
  searchValue?: string;
  showSearchTitle?: boolean;
  placeholder?: string;
  promptSuggestion?: string;
  onChange: (e: React.ChangeEvent<TextFieldType>) => void;
  onSubmit?: (term: string, meta?: { viaRotatingPrompt?: boolean }) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onPromptHoverChange?: (hovering: boolean) => void;
  showRecentSearchDropdown?: boolean;
  elevation?: SurfaceProps['elevation'];
}

const Searchbar: React.FC<SearchbarProps> = ({
  searchValue,
  showSearchTitle,
  placeholder,
  promptSuggestion,
  onChange,
  onSubmit,
  onClear,
  onFocus,
  onBlur,
  onPromptHoverChange,
  showRecentSearchDropdown = false,
  elevation = 0,
  className,
  ref,
  ...props
}) => {
  const { t } = useTranslation();
  const { terms: pastSearchTerms } = useAtomValue(searchAtom);
  const searchDispatch = useSearchDispatch();
  const internalRef = useRef<HTMLDivElement>(null);
  const [suggestionsIsOpen, setSuggestionsIsOpen] = useState(false);

  const mergedRef = (node: HTMLDivElement) => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const trimmedSearchValue = searchValue?.trim() ?? '';
  const showPromptPlaceholder = !!promptSuggestion && !trimmedSearchValue;
  const staticPlaceholder = placeholder ?? t('Search.searchForResources');
  const textFieldPlaceholder = showPromptPlaceholder ? ' ' : staticPlaceholder;
  const searchPromptSuggestionId = 'search-prompt-suggestion';

  const onFocusSearchInput = () => {
    if (showRecentSearchDropdown) setSuggestionsIsOpen(true);
  };

  const closeSuggestions = () => {
    if (showRecentSearchDropdown) setSuggestionsIsOpen(false);
  };

  const onSearchAreaBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget;
    if (next instanceof Node && internalRef.current?.contains(next)) return;
    closeSuggestions();
  };

  const onFormFocus = () => {
    onFocus?.();
  };

  const onFormBlur = (e: React.FocusEvent<HTMLFormElement>) => {
    const next = e.relatedTarget;
    if (next instanceof Node && e.currentTarget.contains(next)) return;
    onBlur?.();
  };

  const submitSearch = (
    term: string,
    meta?: { viaRotatingPrompt?: boolean },
  ) => {
    if (showRecentSearchDropdown) {
      searchDispatch.saveTerm(term);
      setSuggestionsIsOpen(false);
    }
    onSubmit?.(term, meta);
  };

  const submitVisiblePromptSuggestion = () => {
    if (!promptSuggestion) return;
    // Submit the currently visible prompt (prop), not a snapshot from focus
    // time — rotation may tick before parent pause state applies.
    submitSearch(promptSuggestion, {
      viaRotatingPrompt: true,
    });
  };

  const onSubmitSearch: React.SubmitEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    if (trimmedSearchValue) {
      submitSearch(trimmedSearchValue);
      return;
    }
    submitVisiblePromptSuggestion();
  };

  const onDoubleClickSearchInput = () => {
    if (!showPromptPlaceholder) return;
    submitVisiblePromptSuggestion();
  };

  const onClickSuggestion = (suggestion: string) => {
    onChange({
      target: { value: suggestion },
    } as React.ChangeEvent<TextFieldType>);
    submitSearch(suggestion);
  };

  const onClickRemoveSuggestion = (suggestion: string) => {
    if (showRecentSearchDropdown) {
      searchDispatch.removeTerm(suggestion);
    }
  };

  useClickOutside(internalRef, closeSuggestions);

  return (
    <div ref={mergedRef} className="w-full" onBlur={onSearchAreaBlur}>
      <Surface
        {...props}
        elevation={elevation}
        className={cn(
          `relative flex w-full flex-col gap-2 overflow-visible`,
          className,
        )}
      >
        {showSearchTitle && (
          <Typography className="mb-2 font-bold!">
            {t('Generic.search')}
          </Typography>
        )}
        <form
          onSubmit={onSubmitSearch}
          onFocus={onFormFocus}
          onBlur={onFormBlur}
          className={`${showSearchTitle ? 'mb-2' : ''} relative`}
          onMouseEnter={() => onPromptHoverChange?.(true)}
          onMouseLeave={() => onPromptHoverChange?.(false)}
        >
          <div className="relative">
            <TextField
              aria-describedby={
                showPromptPlaceholder ? searchPromptSuggestionId : undefined
              }
              aria-label={t('Generic.search')}
              className="[&_.cfa-helper-text-wrapper]:pt-0! [&_input]:pr-[60px]"
              data-testid="Search"
              fullWidth
              onChange={onChange}
              onClick={onFocusSearchInput}
              onDoubleClick={onDoubleClickSearchInput}
              onFocus={onFocusSearchInput}
              placeholder={textFieldPlaceholder}
              textAlign="start"
              value={searchValue}
            />
            {showPromptPlaceholder && promptSuggestion && (
              <>
                <span className="sr-only" id={searchPromptSuggestionId}>
                  {t('Search.suggestedPromptSubmit', {
                    suggestion: promptSuggestion,
                  })}
                </span>
                <SearchbarRotatingPromptPlaceholder text={promptSuggestion} />
              </>
            )}
          </div>
          <button
            type="submit"
            className="absolute top-[14px] right-[14px] border-none bg-none p-0 leading-none"
            data-testid="SearchMagnifyButton"
          >
            <IconSearch
              className={cn(
                'size-[18px]',
                trimmedSearchValue || showPromptPlaceholder
                  ? 'text-grayscale-gray6'
                  : 'text-grayscale-gray3',
              )}
            />
          </button>
          {trimmedSearchValue && (
            <IconX
              className="absolute top-[14px] right-[40px] size-[18px] cursor-pointer"
              onClick={onClear}
              data-testid="ClearSearchButton"
            />
          )}
        </form>
        {suggestionsIsOpen && (
          <SearchbarSuggestionsDropdown
            onRemoveSuggestion={onClickRemoveSuggestion}
            onSetSuggestion={onClickSuggestion}
            suggestions={pastSearchTerms}
          />
        )}
      </Surface>
    </div>
  );
};

export default Searchbar;
